import { authFetch } from '../../../utils/authFetch';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

interface Outcome {
  code: string;
  displayName: string;
  description: string;
  icon: string;
  apps: string[];
  /** Resolved app names (GET /api/focuses); absent in older responses. */
  unlockedApps?: Array<{ appId: string; displayName: string }>;
  category: string;
  pricing: {
    includedCredits: number;
    trialDays: number;
  };
}

interface WorkspaceOutcome {
  // The response field is `code`; `focusCode` is the column name. Reading
  // the column name here made every enabled-outcome lookup undefined and
  // sent DELETE to /focuses/undefined.
  code: string;
  displayName: string;
  status: 'trialing' | 'active' | 'canceled' | 'expired';
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  apps: string[];
}

interface OutcomesTabProps {
  workspaceId: string;
  authToken: string;
  /** Workspace type: 'personal' lists personal outcomes, anything else business ones. */
  workspaceType?: string;
  /** The caller's DB role in this workspace. Read from GET /api/workspaces/:id when omitted. */
  callerRole?: string | null;
}

/** The 402 UPGRADE_REQUIRED payload's parts the tab uses (CONTRACT-central-v2 §3). */
interface UpgradeRequirement {
  outcomeCode: string;
  outcomeName: string;
  requiredPlan: { code: string; name?: string | null } | null;
  billingAction: string | null;
}

/** The catalog mode for a workspace type: personal outcomes only in a personal workspace. */
export function outcomeModeFor(workspaceType: string | undefined | null): 'personal' | 'business' | null {
  if (!workspaceType) return null;
  return workspaceType === 'personal' ? 'personal' : 'business';
}

/** Only owners and admins may enable or disable outcomes (Central answers 403 OUTCOME_PERMISSION_REQUIRED otherwise). */
export function canManageOutcomes(role: string | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

export interface RemovalImpact {
  /** Apps this outcome alone brings — they disappear for every member on removal. */
  appsLost: string[];
  /** Apps another held outcome still brings — they stay. */
  appsKept: string[];
  /** Included monthly credits that stop (catalog value). */
  creditsLost: number;
  isTrial: boolean;
}

/**
 * What removing `code` costs, from the data the tab already holds: an app is
 * lost only when no OTHER held outcome brings it (outcome→apps is
 * many-to-many; Central re-projects apps from the remaining outcomes), and
 * the outcome's included credits stop at once.
 */
export function removalImpact(code: string, enabled: WorkspaceOutcome[], catalog: Outcome[]): RemovalImpact {
  const target = enabled.find((o) => o.code === code);
  const entry = catalog.find((c) => c.code === code);
  const apps = target?.apps ?? entry?.apps ?? [];
  const others = new Set(enabled.filter((o) => o.code !== code).flatMap((o) => o.apps));
  const name = (appId: string) => entry?.unlockedApps?.find((a) => a.appId === appId)?.displayName ?? appId;
  return {
    appsLost: apps.filter((a) => !others.has(a)).map(name),
    appsKept: apps.filter((a) => others.has(a)).map(name),
    creditsLost: entry?.pricing.includedCredits ?? 0,
    isTrial: target?.status === 'trialing',
  };
}

// SVG icons for outcomes (mapped from Lucide icon names)
const OUTCOME_ICONS: Record<string, JSX.Element> = {
  home: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  users: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  'message-circle': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  'trending-up': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  briefcase: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  calendar: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  'graduation-cap': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
    </svg>
  ),
  shield: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
};

/**
 * Outcomes settings tab
 *
 * - Lists the outcomes this workspace holds (status, trial, apps, credits).
 * - Offers only the outcomes of this workspace's mode (`/api/focuses?mode=`):
 *   a personal workspace is never offered business outcomes and vice versa
 *   (those could only ever answer 402 with requiredPlan 'unknown').
 * - Owners and admins manage; everyone else gets a read-only view.
 * - Removing asks for confirmation and states the impact (apps lost, credits).
 * - A 402 UPGRADE_REQUIRED names the plan that covers the outcome and links to
 *   the plan picker instead of a bare error toast.
 */
export function OutcomesTab({ workspaceId, authToken, workspaceType, callerRole }: OutcomesTabProps) {
  const [enabledOutcomes, setEnabledOutcomes] = useState<WorkspaceOutcome[]>([]);
  const [availableOutcomes, setAvailableOutcomes] = useState<Outcome[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [actionOutcome, setActionOutcome] = useState<string | null>(null);
  const [resolvedRole, setResolvedRole] = useState<string | null | undefined>(callerRole);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState<UpgradeRequirement | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  const canManage = canManageOutcomes(resolvedRole);

  useEffect(() => {
    setResolvedRole(callerRole);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, workspaceType, callerRole]);

  async function loadData() {
    try {
      // The workspace's type and the caller's role come from the page when it
      // has them; otherwise one read of the workspace answers both.
      let type = workspaceType;
      if (type === undefined || callerRole === undefined) {
        try {
          const wsRes = await authFetch(`/api/workspaces/${workspaceId}`, authToken);
          if (wsRes.ok) {
            const ws = await wsRes.json();
            if (type === undefined && typeof ws?.workspace?.type === 'string') {
              type = ws.workspace.type;
            }
            if (callerRole === undefined) setResolvedRole(ws?.membership?.role ?? null);
          } else if (callerRole === undefined) {
            setResolvedRole(null);
          }
        } catch {
          if (callerRole === undefined) setResolvedRole(null);
        }
      }

      const mode = outcomeModeFor(type);
      const [enabledRes, availableRes] = await Promise.all([
        authFetch(`/api/workspaces/${workspaceId}/focuses`, authToken),
        authFetch(mode ? `/api/focuses?mode=${mode}` : '/api/focuses', authToken),
      ]);

      if (enabledRes.ok) {
        const data = await enabledRes.json();
        // `GET /api/workspaces/:id/focuses` answers { ok, outcomes } — the
        // user-facing term — and has never answered `focuses`. Reading the
        // internal name produced [] on every call, so Settings → Outcomes
        // showed "No outcomes enabled yet" for a workspace holding ten, and
        // no amount of correct persistence could make it render one.
        setEnabledOutcomes(data.outcomes || []);
      }

      if (availableRes.ok) {
        const data = await availableRes.json();
        setAvailableOutcomes(data.outcomes || []);
      }
    } catch {
      toast.error('Failed to load outcomes');
    } finally {
      setIsLoading(false);
    }
  }

  function openAddModal() {
    setUpgrade(null);
    setAddError(null);
    setShowAddModal(true);
  }

  async function handleAddOutcome(code: string) {
    setActionOutcome(code);
    setUpgrade(null);
    setAddError(null);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/focuses`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The route reads `code` and answers 400 'code is required'
        // otherwise. `focusCode` is the COLUMN name, not the request field.
        body: JSON.stringify({ code }),
      });

      if (response.ok) {
        toast.success('Outcome added');
        setShowAddModal(false);
        loadData();
        return;
      }
      const error = await response.json().catch(() => ({}));
      if (response.status === 402 && error?.code === 'UPGRADE_REQUIRED') {
        // Nothing was saved. Name the plan that covers it and offer the picker.
        const outcome = availableOutcomes.find((o) => o.code === code);
        setUpgrade({
          outcomeCode: code,
          outcomeName: outcome?.displayName ?? code,
          requiredPlan: error?.upgrade?.requiredPlan ?? null,
          billingAction: error?.upgrade?.billingAction ?? null,
        });
        return;
      }
      setAddError(
        error?.code === 'OUTCOME_PERMISSION_REQUIRED'
          ? 'Only owners and admins can change outcomes.'
          : error?.error || 'Failed to add outcome',
      );
    } catch {
      setAddError('Failed to add outcome');
    } finally {
      setActionOutcome(null);
    }
  }

  async function handleRemoveOutcome(code: string) {
    setActionOutcome(code);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/focuses/${code}`, authToken, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Outcome removed');
        setEnabledOutcomes((prev) => prev.filter((o) => o.code !== code));
        setConfirmRemove(null);
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(
          error?.code === 'OUTCOME_PERMISSION_REQUIRED'
            ? 'Only owners and admins can change outcomes.'
            : error?.error || 'Failed to remove outcome',
        );
      }
    } catch {
      toast.error('Failed to remove outcome');
    } finally {
      setActionOutcome(null);
    }
  }

  // Get outcomes not yet enabled
  const enabledCodes = new Set(enabledOutcomes.map((o) => o.code));
  const notEnabledOutcomes = availableOutcomes.filter((o) => !enabledCodes.has(o.code));

  // PR-D1: outcomes no longer carry per-outcome pricing — plans price
  // the workspace. Credit contribution remains outcome-scoped.
  const totalCredits = enabledOutcomes.reduce((sum, enabled) => {
    const available = availableOutcomes.find((a) => a.code === enabled.code);
    return sum + (available?.pricing.includedCredits || 0);
  }, 0);

  const plansHref = `/workspace/${encodeURIComponent(workspaceId)}/settings/billing?view=plans`;
  const removing = confirmRemove ? enabledOutcomes.find((o) => o.code === confirmRemove) : null;
  const impact = confirmRemove ? removalImpact(confirmRemove, enabledOutcomes, availableOutcomes) : null;

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-8 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Active Outcomes</h3>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{enabledOutcomes.length}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Included Credits</h3>
            <p className="text-2xl font-semibold text-gray-900 mt-1">
              {totalCredits.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Active Outcomes */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Active Outcomes</h2>
          {canManage && (
            <button
              onClick={openAddModal}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
            >
              Add Outcome
            </button>
          )}
        </div>
        {!canManage && (
          <p className="px-6 pt-4 text-sm text-gray-500" data-testid="outcomes-read-only">
            Only workspace owners and admins can add or remove outcomes.
          </p>
        )}
        <div className="divide-y divide-gray-100">
          {enabledOutcomes.map((outcome) => {
            const details = availableOutcomes.find((a) => a.code === outcome.code);
            const isTrial = outcome.status === 'trialing';

            return (
              <div key={outcome.code} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                      {OUTCOME_ICONS[details?.icon || 'briefcase'] || OUTCOME_ICONS.briefcase}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-medium text-gray-900">
                          {outcome.displayName}
                        </h3>
                        {isTrial && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                            Trial
                            {outcome.trialDaysRemaining !== null && (
                              <> &middot; {outcome.trialDaysRemaining}d left</>
                            )}
                          </span>
                        )}
                        {outcome.status === 'active' && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{details?.description}</p>

                      {/* Included tools */}
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 mb-1.5">Included tools:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {outcome.apps.map((app) => (
                            <span
                              key={app}
                              className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600 capitalize"
                            >
                              {app}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Credits included — outcomes are not billed
                          per-outcome (PR-D1); plans price the workspace. */}
                      {details && details.pricing.includedCredits > 0 && (
                        <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                          <span>{details.pricing.includedCredits} credits included</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {canManage && (
                    <button
                      onClick={() => setConfirmRemove(outcome.code)}
                      disabled={actionOutcome === outcome.code}
                      className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-50"
                      title="Remove outcome"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {enabledOutcomes.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              {canManage ? 'No outcomes enabled yet. Add an outcome to get started.' : 'No outcomes enabled yet.'}
            </div>
          )}
        </div>
      </div>

      {/* Remove confirmation, with what it costs */}
      {removing && impact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div role="dialog" aria-modal="true" aria-label={`Remove ${removing.displayName}?`} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove {removing.displayName}?</h3>
            <ul className="mb-4 space-y-2 text-sm text-gray-700" data-testid="outcome-removal-impact">
              {impact.appsLost.length > 0 ? (
                <li>
                  Every member loses access to <strong>{impact.appsLost.join(', ')}</strong> right away.
                </li>
              ) : (
                <li>No apps are lost: other outcomes still include them.</li>
              )}
              {impact.appsKept.length > 0 && <li>Still included by other outcomes: {impact.appsKept.join(', ')}.</li>}
              {impact.creditsLost > 0 && (
                <li>
                  Your monthly included credits drop by <strong>{impact.creditsLost.toLocaleString()}</strong>.
                </li>
              )}
              {impact.isTrial && <li>Its trial ends now; adding it again later may not start a new trial.</li>}
              <li>Your plan and price do not change.</li>
            </ul>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmRemove(null)}
                className="rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Keep it
              </button>
              <button
                onClick={() => handleRemoveOutcome(removing.code)}
                disabled={actionOutcome === removing.code}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {actionOutcome === removing.code ? 'Removing...' : 'Remove outcome'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Outcome Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add Outcome</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {upgrade && (
              <div className="mx-6 mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900 ring-1 ring-amber-200" role="alert" data-testid="outcome-upgrade-required">
                {upgrade.requiredPlan && upgrade.requiredPlan.code !== 'unknown' ? (
                  <>
                    <p>
                      <strong>{upgrade.outcomeName}</strong> needs the{' '}
                      <strong>{upgrade.requiredPlan.name || upgrade.requiredPlan.code}</strong> plan. Nothing was changed.
                    </p>
                    <p className="mt-1">Review the plan and its price, then add the outcome again.</p>
                    <a href={plansHref} className="mt-2 inline-block font-semibold text-emerald-700 hover:underline">
                      Review plans
                    </a>
                  </>
                ) : (
                  <p>
                    <strong>{upgrade.outcomeName}</strong> isn’t offered on any plan for this workspace. Nothing was changed.
                  </p>
                )}
              </div>
            )}
            {addError && (
              <p className="mx-6 mt-4 text-sm text-red-600" role="alert">
                {addError}
              </p>
            )}
            <div className="flex-1 overflow-y-auto p-6">
              {notEnabledOutcomes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  All outcomes are already enabled.
                </div>
              ) : (
                <div className="space-y-4">
                  {notEnabledOutcomes.map((outcome) => (
                    <div
                      key={outcome.code}
                      className="p-4 border border-gray-200 rounded-lg hover:border-emerald-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0">
                            {OUTCOME_ICONS[outcome.icon] || OUTCOME_ICONS.briefcase}
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">
                              {outcome.displayName}
                            </h4>
                            <p className="text-sm text-gray-500 mt-0.5">{outcome.description}</p>
                            <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                              {outcome.pricing.includedCredits > 0 && (
                                <span>{outcome.pricing.includedCredits} credits</span>
                              )}
                              {outcome.pricing.trialDays > 0 && <span>{outcome.pricing.trialDays}-day trial</span>}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddOutcome(outcome.code)}
                          disabled={actionOutcome === outcome.code}
                          className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {actionOutcome === outcome.code ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
